import CancelIcon from "@material-ui/icons/Cancel";
import React, { useEffect, useCallback, useState, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { Select } from 'antd';
import { isEqual, omit, functions } from "lodash";
import classnames from 'classnames';
import Fuse from 'fuse.js';

import styles from './OverviewMap.module.css';
import { SearchOutlined } from "@material-ui/icons";

let markersAdded;

function OverviewMap({ options, className, formattedSites, locations=[] }) {
  const ref = useRef();
  const [ map, setMap ] = useState(null);
  const [ sitesPanel, setSitesPanel ] = useState(null);
  const [ markers, setMarkers ] = useState(null);
  const [ selectedMarker, setSelectedMarker ] = useState(null);
  
  const defaultMarkerColor = "red";
  const highlightMarkerColor = "#000000";
  
  const [ searchSiteSelceted, setSearchSiteSelected ] = useState(null);
  const siteOptions = useMemo(() => formattedSites.map(site => ({ value: parseInt(site.site_id), label: site.company + " " + site.site_name })), [ formattedSites ]);
  const fuseSearch = useMemo(() => new Fuse(siteOptions, { keys: ['label'] }), [ siteOptions ]);
  const [siteOptionsFiltered, setSiteOptionsFiltered] = useState([]);
  
  const isMobile = useCallback(() => window.innerWidth <= 600, []);
  
  // center with offset for side panel which shows basic site info
  const offsetCenter = useCallback((map, latlng, offsetX, offsetY) => {
    let point1 = map.getProjection().fromLatLngToPoint(latlng);
    let point2 = new window.google.maps.Point(
      ( offsetX / Math.pow(2, map.getZoom()) ) || 0,
      ( offsetY / Math.pow(2, map.getZoom()) ) || 0
    );  
    map.panTo(map.getProjection().fromPointToLatLng(new window.google.maps.Point(
      point1.x - point2.x,
      point1.y + point2.y
      )));
  }, []);

  const bounceMarker = useCallback((marker, { delay }={ delay: 0 }) => {
    if(!window.google?.maps) { return }
    setTimeout(() => {
      marker.setAnimation(window.google.maps.Animation.BOUNCE);
      setTimeout(function(){ marker.setAnimation(null); }, 1500);
    }, delay);
  }, []);

  const getMarkerIcon = useCallback((fillColor="red") => ({
    path: "M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 256c-35.3 0-64-28.7-64-64s28.7-64 64-64s64 28.7 64 64s-28.7 64-64 64z",
    fillColor,
    fillOpacity: 1,
    anchor: new window.google.maps.Point(192, 128),
    strokeWeight: 1,
    strokeColor: "white",
    scale: 0.06,
  }), []);

  const selectMarker = useCallback((marker, allMarkers) => {
    setSelectedMarker(marker);
    // lines below are just for UX/UI
    offsetCenter(map, marker.position, ...(isMobile() ? [0, 100] : [100, 0]));
    // bounce marker
    bounceMarker(marker);
    // change selected marker color
    marker.setIcon(getMarkerIcon(highlightMarkerColor));
    // reset other markers color
    allMarkers.filter(_marker => _marker.position.lat() !== marker.position.lat() && _marker.position.lng() !== marker.position.lng()).forEach(_marker => _marker.setIcon(getMarkerIcon(defaultMarkerColor)));
  }, [offsetCenter, map, isMobile, getMarkerIcon, bounceMarker]);
  
  // user scroll to the bottom, add markers to the map
  const addMarkers = useCallback(() => {
    if(markersAdded || !window.google?.maps || !map) { return }
    // map marker data
    const locs = locations.map((place) => ({
      coords: {
        lat: parseFloat(place.GPS.split(", ")[0]),
        lng: parseFloat(place.GPS.split(", ")[1]),
      }
    }));
    
    // filter out markers having the same coordinate as other markers
    const markersArr = locs.filter((loc, index) => locs.findIndex(_loc => _loc.coords.lat === loc.coords.lat && _loc.coords.lng === loc.coords.lng) === index);
    
    let time; // set time for each length
    markersArr.length > 4
    ? markersArr.length > 9
        ? markersArr.length > 19
          ? markersArr.length > 59
          ? (time = 50)
            : (time = 200)
            : (time = 500)
        : (time = 800)
        : (time = 1200);			

        // add markers to the map
    const _markers = [];
    for(let [i, markerObj] of markersArr.entries()) {
      // eslint-disable-next-line no-loop-func
      window.setTimeout(() => {
        const marker = new window.google.maps.Marker({
          map,
          position: markerObj.coords,
          animation: window.google.maps.Animation.DROP,
          icon: getMarkerIcon(defaultMarkerColor)
        });
        // eslint-disable-next-line no-loop-func
        marker.addListener("click", () => {
          selectMarker(marker, _markers);
          const location = locations.find(loc => marker.position.lat() === parseFloat(loc.GPS.split(", ")[0]) && marker.position.lng() === parseFloat(loc.GPS.split(", ")[1]));
          const siteId = location.site_id;
          setSearchSiteSelected(null);
        });
        _markers.push(marker);
        setMarkers([..._markers]);
      }, i * time);
    }
    markersAdded = true;
  }, [map, locations, getMarkerIcon, selectMarker]);
  
  // Panel Matrics
  const matrics = useMemo(() => ([
    { key: 'power', label: 'Plant Capacity (kW)' },
    { key: 'solar_power', label: 'Solar Power (kW)' },
    { key: 'irr', label: 'Irradiance (kW/m2)' },
    { key: 'brut_PR', label: 'PR Yesterday (%)' },
    { key: 'solar_total', label: 'Solar Energy (kWh)' },
    { key: 'grid_total', label: 'Grid Energy (kWh)' },
  ]), []);
  const matricItem = useCallback((site, { key, label }) => (
    <div key={key} className={styles.matricItem}>
      <div className={styles.value}>
        {parseFloat(site[key]).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })}
    </div>
      <div className={styles.label}>{label}</div>
    </div>
  ), []);
  const getSiteItem = useCallback(site => (
    <div key={site.site_id} className={styles.site}>
      <h2 className={styles.siteName}>
        <Link to={`/solar/dashboard/${site.site_id}`} style={site.isTestSite ? { color: "#ffc709" } : { color: "#12a8e0" }}>
          {site.company} {site.site_name}
        </Link>
      </h2>
      <div className={styles.matrics}>
        {matrics.map(item => matricItem(site, item))}
      </div>
    </div>
  ), [ matricItem, matrics ]);
  
  const handleClosePanel = useCallback(() => {
    // remove highlight from markers
    markers.filter(marker => marker.getIcon()).forEach(marker => marker.setIcon(getMarkerIcon("red")));
    setSelectedMarker(null);
    setSearchSiteSelected(null);
  }, [ markers, getMarkerIcon ]);
  
  // if marker selected or deselected, update sites showing in side panel
  useEffect(() => {
    if(!selectedMarker) { setSitesPanel(null) }
    if(selectedMarker) {
      const selectedLocations = locations.filter(loc => selectedMarker.position.lat() === parseFloat(loc.GPS.split(",")[0]) && selectedMarker.position.lng() === parseFloat(loc.GPS.split(",")[1]));
      const selectedSites = selectedLocations.map(loc => formattedSites.find(site => parseInt(site.site_id) === parseInt(loc.site_id)));
      setSitesPanel(selectedSites);
    }
  }, [ selectedMarker, locations, formattedSites ]);
  
  useEffect(() => {
    const updateMapState = () => {
      if(map) { return }
      if(!window.google?.maps) { return }
      const mapObj = window.map = new window.google.maps.Map(ref.current, options);
      setMap(mapObj);
    };
    // google maps not yet imported
    const googleMapScriptId = "google-maps";
    if(!document.querySelector(`#${googleMapScriptId}`)) {
      const script = document.createElement(`script`);
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_API_KEY_GOOGLEMAP}`;
      script.id = googleMapScriptId;
      script.onload = () => updateMapState();
      document.head.append(script);
    }
    // google maps imported
    if(document.querySelector(`#${googleMapScriptId}`)) {
      updateMapState();
    }
  }, [options, map]);
  
  /* Detecting if OverviewMap shows in viewport while scrolling */
  const isScrolledIntoView = useCallback((elm) => {
    if(!elm) { return false }
    let elmRect = elm.getBoundingClientRect();
    let isVisible = (elmRect.top < window.innerHeight && elmRect.top > 0) || (elmRect.bottom < window.innerHeight && elmRect.bottom > 0);
    return isVisible;
  }, [])
  const handleScroll = useCallback(() => {
    if (isScrolledIntoView(ref.current) && (locations.length > 0) && !markers) {
      addMarkers();
    }
  }, [ markers, addMarkers, locations.length, isScrolledIntoView ]);
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [ handleScroll ]);

  const handleSiteSelectChange= useCallback(siteId => {
    setSearchSiteSelected(parseInt(siteId));
    const site = locations.find(site => parseInt(site.site_id) === parseInt(siteId));
    const siteLat = parseFloat(site.GPS.split(",")[0].trim());
    const siteLng = parseFloat(site.GPS.split(",")[1].trim());
    const marker = markers.find(marker => marker.position.lat() === siteLat && marker.position.lng() === siteLng);
    if(!marker) { return console.log('no marker found'); }
    selectMarker(marker, markers);
  }, [locations, markers, selectMarker]);

  const handleSiteSelectSearch = useCallback(search => {
    setSearchSiteSelected(null);
    const searchResults = search.trim() ? fuseSearch.search(search).map(result => result.item) : [];
    setSiteOptionsFiltered(searchResults);
  }, [ fuseSearch ]);

  // reset markersAdded
  useEffect(() => () => { markersAdded=false }, []);
  
  return (
    <div className="panel panel-default">
      <div className="panel-heading ">
        <h2 className="panel-title">Sites map</h2>
      </div>
      <div className={classnames(`panel-body`, styles.mapPanelContainer)}>
        
        {/* Google Map */}
        <div
          style={{ height: "90vh", width: "100%" }}
          {...{ ref, className }}
          />
        
        {/* Search Box */}
        <Select showSearch className={styles.searchBox} size="large" suffixIcon={<SearchOutlined style={{fontSize: '1.8em'}} />} placeholder="Search site..." optionFilterProp="children" value={searchSiteSelceted} onChange={handleSiteSelectChange} onSearch={handleSiteSelectSearch} options={siteOptionsFiltered} notFoundContent={null} filterOption={false} />
        
        {/* Sites Panel */}
        <div className={classnames(styles.mapPanel, { [styles.hide]: !sitesPanel?.length })}>
          {/* Close button */}
          <CancelIcon onClick={handleClosePanel} className={styles.mapPanelCloseBtn} />
          {!!sitesPanel?.length && <div className={styles.mapPanelContent}>
            {/* Sites */}
            {sitesPanel.map(site => getSiteItem(site))}
          </div>}
        </div>
      </div>
    </div>
  );
}
function shouldNotUpdate(props, nextProps) {
  const [funcs, nextFuncs] = [functions(props), functions(nextProps)];
  const noPropChange = isEqual(omit(props, funcs), omit(nextProps, nextFuncs));
  const noFuncChange =
    funcs.length === nextFuncs.length &&
    funcs.every((fn) => props[fn].toString() === nextProps[fn].toString());
  return noPropChange && noFuncChange;
}
export default React.memo(OverviewMap, shouldNotUpdate);
